import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import BrandLogo from '../components/brand/BrandLogo';
import { toast } from 'sonner';
import api from '../lib/api';
import AuthFooter from '../components/shared/AuthFooter';

/**
 * ForgotPassword — asks the seller for their registered email, POSTs it to
 * /auth/forgot-password, and swaps to a success state. The API response is
 * intentionally uniform whether or not the account exists (enumeration-safe),
 * so we always show the same "check your inbox" screen after a submit.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen mesh-bg flex items-center justify-center p-6">
      <div className="w-full max-w-md fade-up">
        <Link to="/" className="flex items-center justify-center mb-8">
          <BrandLogo height={38} />
        </Link>

        <div className="fab-card p-8" data-testid="forgot-password-card">
          {!sent ? (
            <>
              <h1 className="font-display text-2xl font-bold text-fab-text">Forgot your password?</h1>
              <p className="text-[13px] text-fab-text-secondary mt-1">
                Enter the email you signed up with and we&apos;ll send you a reset link.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="text-[12px] font-semibold text-fab-text-secondary">Registered Email</label>
                  <div className="relative mt-1.5">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fab-text-muted" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@business.com"
                      className="fab-input w-full pl-9 pr-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]"
                      data-testid="forgot-email-input"
                      required
                      autoFocus
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="btn-primary w-full py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="forgot-submit"
                >
                  {loading ? 'Sending link…' : 'Send Reset Link'}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="forgot-password-sent">
              <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h1 className="font-display text-2xl font-bold text-fab-text mt-4">Check your inbox</h1>
              <p className="text-[13px] text-fab-text-secondary mt-2 leading-relaxed">
                If an account exists for <span className="font-mono text-fab-text">{email}</span>,
                we&apos;ve sent a reset link. It expires in 30 minutes.
              </p>
              <p className="text-[12px] text-fab-text-muted mt-3">
                Didn&apos;t get it? Check spam, or{' '}
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  className="text-fab-primary font-semibold hover:underline"
                  data-testid="forgot-try-again"
                >
                  try again
                </button>.
              </p>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-[13px] text-fab-text-secondary font-semibold inline-flex items-center gap-1 hover:text-fab-primary" data-testid="forgot-back-to-login">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Sign in
            </Link>
          </div>
        </div>
        <AuthFooter />
      </div>
    </div>
  );
}
